import { useDispatch, useSelector } from "react-redux";
import { useState, memo, useEffect } from "react";
import TextField from "../common/TextField";
import UMCButton from "../common/UMCButton";
import ToggleButton from "../common/ToggleButton";
import { SearchOutlined } from "@ant-design/icons";
import { PURPLE } from "../../styles/styleUtils";
import { getTranslated, tableListFilter } from "../../utils/CommonUtils";
import { setContentLoading, setExtraContent } from "../../store/reducers/CommonReducer";
import { useWindowSize } from "../customHooks/useWindowSize";
import { setGroupTableSchema, setIsPickupGroupTab, setGroupFilteredList, setHuntGroupTableSchema, setGroupTableList, setHuntGroupFilteredList } from "../../store/reducers/UUMReducer";
import { getTableSchema } from "../../store/actions/CommonActions";
import { UUM_URL, UUM_LOCAL_URL } from "../../utils/Config";
import { getGroupList, deletePickupGroup, getHuntGroupList, deleteHuntingGroup } from "../../store/actions/UUMActions";
import { CREATE, GROUP_TOGGLE_ITEMS, UPDATE, PICKUP_GROUP, HUNTING_GROUP } from "../../utils/Constants";
import CustomTable from "../common/CustomTable";
import confirmModal from "../common/confirmModal";
import PickupGroupDetailView from "./PickupGroupDetailView";
import HuntingGroupDetailView from "./HuntingGroupDetailView";
import { setGroupAndUsers } from "../../store/reducers/UUMReducer"
import { showOxeErrorsByCodesInDialog } from "../../store/actions/OxeActions";

//sample schema

const viewModes = {
    LIST: "list",
    DETAIL: "detail"
}

const groupOperation = {
    CREATE: "create",
    UPDATE: "update"
}

const Groups = () => {

    //local states------------------------------------------------
    const [viewMode, setViewMode] = useState(viewModes.LIST)
    const [selectedGroup, setSelectedGroup] = useState()
    const [isReadOnly, setIsReadOnly] = useState(false) //used
    const [selectedRows, setSelectedRows] = useState([]) //used
    const [operation, setOperation] = useState()
    //------------------------------------------------------------

    //redux store variables---------------------------------------
    //------------------pickup group states-----------------------
    const groupTableSchema = useSelector(state => state.uum.groupTableSchema)
    const groupFilteredList = useSelector(state => state.uum.groupFilteredList)
    const groupTableList = useSelector(state => state.uum.groupTableList)

    //------------------hunt group states-------------------------
    const huntGroupTableSchema = useSelector(state => state.uum.huntGroupTableSchema)
    const huntGroupFilteredList = useSelector(state => state.uum.huntGroupFilteredList)
    const huntGroupTableList = useSelector(state => state.uum.huntGroupTableList)
    const groupAndUsers = useSelector(state => state.uum.groupAndUsers)

    const isPickupGroupTab = useSelector((state) => state.uum.isPickupGroupTab)
    const dict = useSelector(state => state.common.dict)

    const oxeErrorCodesFromStore = useSelector(state => state.common.oxeErrorCodes)
    //------------------------------------------------------------

    const dispatch = useDispatch()

    //to adjust window size
    const [width, height] = useWindowSize()

    //useEffects--------------------------------------------------
    //Make loader to false after loading
    useEffect(() => {

        if ((groupTableSchema?.length && groupTableList) || (huntGroupTableSchema?.length && huntGroupTableList)) {
            dispatch(setContentLoading(false))
        }
    }, [groupTableSchema, groupTableList, huntGroupTableSchema, huntGroupTableList])

    //to set the page type - list/detail/summary
    useEffect(() => {
        let extraContent = []
        switch (viewMode) {
            case viewModes.LIST:
                extraContent = ["refresh", "help"];
                break;
            case viewModes.DETAIL:
                extraContent = ["help"];
                break;
            default:
                break
        }
        dispatch(setExtraContent(extraContent))
    }, [viewMode])

    //Whenever tab change happens, get table data
    useEffect(() => {
        getTableData()
    }, [isPickupGroupTab])

    //functions-----------------------------------------------------
    const getTableData = () => {
        dispatch(setContentLoading(true))

        if (isPickupGroupTab) {
            // Get Pickup Group Table schema
            !(groupTableSchema?.length) && dispatch(getTableSchema({ url: `${UUM_URL}/schema/list/pickupgroup`, action: setGroupTableSchema }))

            // Get groups Table data
            dispatch(getGroupList())

        } else {
            //Get Hunt group Table schema
            !(huntGroupTableSchema?.length) && dispatch(getTableSchema({ url: `${UUM_URL}/schema/list/huntinggroup`, action: setHuntGroupTableSchema }))

            // Get hunt groups Table data
            dispatch(getHuntGroupList())
        }
    }

    const handleTableSearch = (searchVal) => {
        let result = [];

        if (isPickupGroupTab) {

            const filteredArr = tableListFilter(groupTableList, searchVal)
            if (filteredArr.length === 0 && groupAndUsers !== '') {
                result = doDeepSearch(groupAndUsers, searchVal)
            }
            (filteredArr.length !== 0) ? dispatch(setGroupFilteredList(filteredArr)) : dispatch(setGroupFilteredList(result))

        } else {
            const filteredArray = tableListFilter(huntGroupTableList, searchVal)

            if (filteredArray.length === 0 && groupAndUsers !== '') {
                result = doDeepSearch(groupAndUsers, searchVal)
            }
            (filteredArray.length !== 0) ? dispatch(setHuntGroupFilteredList(filteredArray)) : dispatch(setHuntGroupFilteredList(result))
        }
    }

    const doDeepSearch = (groupAndUsers, searchVal) => {
        const result = [];
        groupAndUsers.forEach(group => {
            const attrs = group.attribute;

            const groupId = isPickupGroupTab ? "Pick_Up_Group_Name" : "Hunt_Group_Name"

            const listAttr = attrs.find(attr => attr.id === "Names" && Array.isArray(attr.listValue));
            const nameAttr = attrs.find(attr => attr.id === groupId);
            const membersAttr = attrs.find(attr => attr.id === "Number_Of_Members");
            const directoryNumberAttr = attrs.find(attr => attr.id === "Directory_Number");

            if (isPickupGroupTab && listAttr && listAttr.listValue.some(name => name.toLowerCase().includes(searchVal.toLowerCase()))) {
                result.push({
                    attribute: {
                        Pick_Up_Group_Name: nameAttr?.value || "",
                        Number_Of_Members: membersAttr?.value || ""
                    },
                    id: group.id
                });
            } else {
                if (listAttr && listAttr.listValue.some(name => name.toLowerCase().includes(searchVal.toLowerCase()))) {
                    result.push({
                        attribute: {
                            Hunt_Group_Name: nameAttr?.value || "",
                            Number_Of_Members: membersAttr?.value || "",
                            Directory_Number: directoryNumberAttr?.value || ""
                        },
                        id: group.id
                    });
                }
            }
        });
        return result;
    }

    const setListViewMode = (value) => {
        value && getTableData()
        setViewMode(value ? viewModes.LIST : viewModes.DETAIL)
    }

    const handleRowSelect = (rowData) => {
        setSelectedGroup(rowData.key)
        setIsReadOnly(rowData.modifiable ? false : true)
        setViewMode(viewModes.DETAIL)
        setOperation(groupOperation.UPDATE)
        dispatch(setContentLoading(false))
    }

    const handleRowDelete = (key) => {
        dispatch(setContentLoading(true))
        if (isPickupGroupTab) {
            dispatch(deletePickupGroup(key, (status, resp) => {
                if (status) {
                    getTableData()
                } else {
                    dispatch(setContentLoading(false))
                    dispatch(showOxeErrorsByCodesInDialog(resp))
                }

            }))
        } else {
            dispatch(deleteHuntingGroup(key, (status, resp) => {
                if (status) {
                    getTableData()
                } else {
                    dispatch(setContentLoading(false))
                    dispatch(showOxeErrorsByCodesInDialog(resp))
                }
            }))
        }
    }

    const showDeleteConfirm = (key) => {
        confirmModal({
            title: getTranslated(dict, "MSG_SURE_DEL_GROUP"),
            content: getTranslated(dict, "MSG_CONFIRM"),
            error: true,
            onOk() {
                handleRowDelete(key)
            }
        })
    }

    const getGroupsHeader = () => {
        return <div className="inner-tab-hd-container">
            <TextField
                handleChange={handleTableSearch}
                className="user-search-groups"
                prefix={<SearchOutlined style={{ fontSize: "10px", color: PURPLE }} />}
                placeholder={getTranslated(dict, "LABEL_GLOBAL_SEARCH_GROUP")}
            />

            <UMCButton label={getTranslated(dispatchEvent, "LABEL_CREATE")}
                onClick={() => {
                    setListViewMode(false);
                    setOperation(groupOperation.CREATE)
                }}
                type="primary" className="cont-header-btn" />

            <ToggleButton
                options={GROUP_TOGGLE_ITEMS}
                className="cont-header-btn last-option"
                value={isPickupGroupTab}
                handleChange={(value) => dispatch(setIsPickupGroupTab(value))}
            />
        </div>
    }

    const getTable = () => {
        return <CustomTable
            schema={isPickupGroupTab ? groupTableSchema : huntGroupTableSchema}
            filteredData={isPickupGroupTab ? groupFilteredList : huntGroupFilteredList}
            tableData={isPickupGroupTab ? groupTableList : huntGroupTableList}
            updateTableAction={isPickupGroupTab ? setGroupFilteredList : setHuntGroupFilteredList}
            handleRowSelect={handleRowSelect}
            handleRowDelete={(key) => showDeleteConfirm(key)}
            height={height <= 585 ? "22em" : "60vh"}
            bulk={false}
            handleBulkSelection={(rows) => setSelectedRows(rows)}
            tabKey={isPickupGroupTab ? PICKUP_GROUP : HUNTING_GROUP}
        />
    }

    //DOM rendering-------------------------------------------------
    return <div id="inner-tab-content">
        {viewMode === viewModes.LIST ?
            <>
                <div style={{ marginBottom: "14px" }}>
                    {getGroupsHeader()}
                </div>
                {getTable()}
            </> :
            isPickupGroupTab ?
                <>
                    <PickupGroupDetailView
                        heading_1={getTranslated(dict, operation === 'create' ? "MSG_CREATE_PICKUP_GROUP" : "LABEL_PICKUP_GROUP")}
                        heading_2={getTranslated(dict, "MSG_SELECT_MEMBERS_GROUP")}

                        backToListView={setListViewMode}
                        changeCreateMode={setListViewMode}
                        setListViewMode={setListViewMode}

                        currentOperation={operation}
                        viewMode={viewMode}
                        groupToEdit={selectedGroup}
                        type={isPickupGroupTab}
                    />
                </> :
                <>
                    <HuntingGroupDetailView
                        heading_1={getTranslated(dict, operation === 'create' ? "MSG_CREATE_HUNTING_GROUP" : "PARAM_HUNTING_GROUP")}
                        heading_2={getTranslated(dict, "MSG_SELECT_MEMBERS_GROUP")}

                        backToListView={setListViewMode}
                        changeCreateMode={setListViewMode}
                        setListViewMode={setListViewMode}

                        currentOperation={operation}
                        viewMode={viewMode}
                        groupToEdit={selectedGroup}
                        type={isPickupGroupTab}
                    />
                </>
        }
    </div>
}

export default memo(Groups) 